/**
 * @typedef {Object} SessionContext
 * @property {string|null} caregiverId
 * @property {string} caregiverName
 * @property {string|null} userToken
 * @property {string|null} userRole
 * @property {string} familyMemberName
 * @property {string|null} familyMemberId
 * @property {string|null} familyMemberClientId
 * @property {string|null} familyClientId
 * @property {string} familyClientName
 * @property {string|null} communityId
 * @property {string} communityName
 * @property {string|null} communityFlag
 * @property {string|null} residentId
 * @property {string|null} caregiverCheckinId
 * @property {string|null} checkinTime
 * @property {string} residentName
 * @property {string|null} shiftId
 * @property {string|null} appVersion
 */

import { jwtDecode } from "jwt-decode";
import { CAREGIVER_ROUTE_PATHS } from "../../pages/caregiver/caregiverRoutes";
import { FAMILY_MEMBER_PATHS as FAMILY_MEMBER_ROUTE_PATHS } from "../../pages/familymember/familymemberRoutes";
import { setToLocalStorage } from "./localStorage";

export const USER_ROLE_CAREGIVER = "caregiver";
export const USER_ROLE_FAMILY = "family_member";

const CAREGIVER_PATHS = new Set(CAREGIVER_ROUTE_PATHS);
const FAMILY_MEMBER_PATHS = new Set(FAMILY_MEMBER_ROUTE_PATHS);

const FAMILY_CLIENT_REQUIRED_PATHS = new Set([
  "/family-home",
  "/family-checklist",
  "/family-vitals",
]);

const COMMUNITY_REQUIRED_PATHS = new Set(["/home", "/caregiver-task", "/checkin", "/new-checkin", "/all-notes", "/resident-home", "/newResident", "/resident-task", "/resident-notes", "/notes", "/add-notes", "/vitals", "/addVitals", "/additionals"]);
const RESIDENT_REQUIRED_PATHS = new Set(["/resident-home", "/newResident", "/resident-task", "/resident-notes", "/notes", "/add-notes", "/vitals", "/addVitals", "/additionals"]);

/**
 * Reads the current user/session values from localStorage.
 * @returns {SessionContext}
 */
export const getSessionContext = () => {
  const caregiverId = localStorage.getItem("caregiverId") || localStorage.getItem("CaregiverId");
  const caregiverName = localStorage.getItem("caregiverName") || "";
  const userToken = localStorage.getItem("user_token");
  const userRole = localStorage.getItem("userRole");
  const familyMemberName = localStorage.getItem("familyMemberName") || "";
  const familyMemberId = localStorage.getItem("familyMemberId");
  const familyMemberClientId = localStorage.getItem("familyMemberClientId");
  const familyClientId = localStorage.getItem("familyClientId");
  const familyClientName = localStorage.getItem("familyClientName") || "";
  const communityId = localStorage.getItem("communityId");
  const communityName = localStorage.getItem("communityName") || "";
  const communityFlag = localStorage.getItem("communityFlag");
  const residentId = localStorage.getItem("ResidentId");
  const caregiverCheckinId = localStorage.getItem("CaregiverCheckinId");
  const checkinTime = localStorage.getItem("CheckinTime");
  const residentName = localStorage.getItem("ResidentName") || "";
  const shiftId = localStorage.getItem("shiftId");
  const appVersion = localStorage.getItem("appVersion");

  return {
    caregiverId,
    caregiverName,
    userToken,
    userRole,
    familyMemberName,
    familyMemberId,
    familyMemberClientId,
    familyClientId,
    familyClientName,
    communityId,
    communityName,
    communityFlag,
    residentId,
    caregiverCheckinId,
    checkinTime,
    residentName,
    shiftId,
    appVersion,
  };
};

export const isFamilyMemberSession = (sessionContext = getSessionContext()) => {
  return sessionContext.userRole === USER_ROLE_FAMILY;
};

export const hasCaregiverSession = (sessionContext = getSessionContext()) => {
  return Boolean(sessionContext.caregiverId) && !isFamilyMemberSession(sessionContext);
};

export const hasAuthenticatedSession = (sessionContext = getSessionContext()) => {
  return Boolean(sessionContext.userToken && (hasCaregiverSession(sessionContext) || isFamilyMemberSession(sessionContext)));
};

export const hasFamilyClientSelection = (sessionContext = getSessionContext()) => {
  return Boolean(sessionContext.familyMemberClientId || sessionContext.familyClientId);
};

export const hasCommunitySelection = (sessionContext = getSessionContext()) => {
  return Boolean(sessionContext.communityId);
};

export const hasActiveResidentSession = (sessionContext = getSessionContext()) => {
  return Boolean(sessionContext.residentId && sessionContext.caregiverCheckinId);
};

export const getStoredUserToken = () => {
  return getSessionContext().userToken;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const JWT_EMAIL_CLAIM_KEYS = ["emailId", "email", "sub", "username", "userName", "preferred_username"];
const JWT_ROLE_CLAIM_KEYS = ["role", "userRole", "authorities", "roles"];

/**
 * Maps API/native role claim values to localStorage userRole.
 */
export const normalizeUserRoleFromClaim = (roleValue) => {
  const role = String(roleValue || "")
    .trim()
    .toUpperCase()
    .replace(/-/g, "_");

  if (role === "FAMILYMEMBER" || role === "FAMILY_MEMBER" || role === "FAMILY") {
    return USER_ROLE_FAMILY;
  }

  if (role === "CAREGIVER") {
    return USER_ROLE_CAREGIVER;
  }

  return "";
};

const normalizeRoleClaimValue = (value) => {
  if (Array.isArray(value)) {
    for (const item of value) {
      const normalized = normalizeUserRoleFromClaim(item);
      if (normalized) {
        return normalized;
      }
    }
    return "";
  }

  if (value && typeof value === "object" && Array.isArray(value.roles)) {
    return normalizeRoleClaimValue(value.roles);
  }

  return normalizeUserRoleFromClaim(value);
};

export const extractEmailFromToken = (token) => {
  if (!token) {
    return "";
  }

  try {
    const decoded = jwtDecode(token);
    for (const claimKey of JWT_EMAIL_CLAIM_KEYS) {
      const claimValue = String(decoded[claimKey] || "")
        .trim()
        .toLowerCase();
      if (EMAIL_REGEX.test(claimValue)) {
        return claimValue;
      }
    }
  } catch {
    return "";
  }

  return "";
};

export const extractUserRoleFromToken = (token) => {
  if (!token) {
    return "";
  }

  try {
    const decoded = jwtDecode(token);

    if (decoded.realm_access?.roles) {
      const normalized = normalizeRoleClaimValue(decoded.realm_access.roles);
      if (normalized) {
        return normalized;
      }
    }

    for (const claimKey of JWT_ROLE_CLAIM_KEYS) {
      if (decoded[claimKey] === undefined) {
        continue;
      }
      const normalized = normalizeRoleClaimValue(decoded[claimKey]);
      if (normalized) {
        return normalized;
      }
    }
  } catch {
    return "";
  }

  return "";
};

export const extractSessionClaimsFromToken = (token) => ({
  email: extractEmailFromToken(token),
  userRole: extractUserRoleFromToken(token),
});

/**
 * Resolves the signed-in user's email for profile/auth APIs.
 * Falls back to JWT claims when userEmail was not persisted at login.
 */
export const getStoredUserEmail = () => {
  const sessionEmail = localStorage.getItem("userEmail") || "";
  const normalizedSessionEmail = String(sessionEmail).trim().toLowerCase();
  if (EMAIL_REGEX.test(normalizedSessionEmail)) {
    return normalizedSessionEmail;
  }

  const token = getStoredUserToken();
  if (!token) {
    return "";
  }

  const emailFromToken = extractEmailFromToken(token);
  if (emailFromToken) {
    setToLocalStorage("userEmail", emailFromToken);
    return emailFromToken;
  }

  return "";
};

export const getDefaultSessionRoute = (sessionContext = getSessionContext()) => {
  if (!sessionContext.userToken) {
    return "/login";
  }

  if (isFamilyMemberSession(sessionContext)) {
    return hasFamilyClientSelection(sessionContext) ? "/family-home" : "/family-select-client";
  }

  if (!hasCaregiverSession(sessionContext)) {
    return "/login";
  }

  if (!hasCommunitySelection(sessionContext)) {
    return "/select-community";
  }

  if (hasActiveResidentSession(sessionContext)) {
    return "/newResident";
  }

  return "/home";
};

export const resolveProtectedRouteRedirect = (pathname, sessionContext = getSessionContext()) => {
  if (!sessionContext.userToken) {
    return "/login";
  }

  if (isFamilyMemberSession(sessionContext)) {
    if (CAREGIVER_PATHS.has(pathname)) {
      return hasFamilyClientSelection(sessionContext) ? "/family-home" : "/family-select-client";
    }

    if (FAMILY_CLIENT_REQUIRED_PATHS.has(pathname) && !hasFamilyClientSelection(sessionContext)) {
      return "/family-select-client";
    }

    if (FAMILY_MEMBER_PATHS.has(pathname)) {
      return "";
    }

    return hasFamilyClientSelection(sessionContext) ? "/family-home" : "/family-select-client";
  }

  if (!hasCaregiverSession(sessionContext)) {
    return "/login";
  }

  if (FAMILY_MEMBER_PATHS.has(pathname)) {
    return "/home";
  }

  if (COMMUNITY_REQUIRED_PATHS.has(pathname) && !hasCommunitySelection(sessionContext)) {
    return "/select-community";
  }

  if (RESIDENT_REQUIRED_PATHS.has(pathname) && !hasActiveResidentSession(sessionContext)) {
    return "/checkin";
  }

  return "";
};
